import React, { Fragment, useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import API from '../../services/API';
import { TabContent, TabPane, Table } from 'reactstrap';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import dayjs from 'dayjs';

const STATUS_META = {
    1: {
        label: 'Check-in',
        className: 'bg-success-subtle text-success border border-success-subtle',
    },
    2: {
        label: 'Check-out',
        className: 'bg-info-subtle text-info border border-info-subtle',
    },
    default: {
        label: '—',
        className: 'bg-light text-muted border',
    },
};

const cardStyle = {
    borderRadius: 18,
    border: '1px solid #e9eef5',
    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.05)',
    background: '#fff',
};

const statCardStyle = {
    ...cardStyle,
    padding: '1rem',
    height: '100%',
};

const sectionTitleStyle = {
    fontSize: '0.72rem',
    fontWeight: 800,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: '#94a3b8',
    marginBottom: 8,
};

const valueStyle = {
    fontSize: '2rem',
    fontWeight: 900,
    color: '#020617',
    lineHeight: 1,
    letterSpacing: '-0.04em',
};

const mutedStyle = {
    color: '#64748b',
    fontSize: '0.86rem',
};

const ImagePreviewModal = ({ image, onClose }) => {
    if (!image) return null;

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(15, 23, 42, 0.72)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 24,
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    maxWidth: '92vw',
                    maxHeight: '92vh',
                    position: 'relative',
                }}
            >
                <button
                    type="button"
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: -14,
                        right: -14,
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        border: 'none',
                        background: '#fff',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                        fontSize: 18,
                        fontWeight: 700,
                        cursor: 'pointer',
                    }}
                >
                    ×
                </button>

                <img
                    src={image}
                    alt="student event"
                    style={{
                        display: 'block',
                        maxWidth: '92vw',
                        maxHeight: '92vh',
                        borderRadius: 18,
                        boxShadow: '0 25px 60px rgba(0,0,0,0.35)',
                        background: '#fff',
                    }}
                />
            </div>
        </div>
    );
};

const SummaryCard = ({ label, value, subtext }) => (
    <div style={statCardStyle}>
        <div style={sectionTitleStyle}>{label}</div>
        <div style={valueStyle}>{value}</div>
        {subtext ? (
            <div style={{ ...mutedStyle, marginTop: 8, lineHeight: 1.35 }}>
                {subtext}
            </div>
        ) : null}
    </div>
);

const EmptyState = ({ title, subtitle }) => (
    <div
        className="text-center"
        style={{
            padding: '4rem 1rem',
            borderRadius: 18,
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px dashed #dbe2ea',
        }}
    >
        <h5 className="mb-2" style={{ fontWeight: 700, color: '#334155' }}>
            {title}
        </h5>
        <div style={mutedStyle}>{subtitle}</div>
    </div>
);

const CustomCheckerDetailPage = () => {
    const queryParams = new URLSearchParams(window.location.search);
    const classId = queryParams.get('id');

    const [className, setClassName] = useState('');
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [activeSubTab] = useState('students');
    const [eventRecords, setEventRecords] = useState([]);
    const [monthlySummary, setMonthlySummary] = useState([]);
    const [loadingEvents, setLoadingEvents] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedClassFilter, setSelectedClassFilter] = useState('ALL');
    const [previewImage, setPreviewImage] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [serverPage, setServerPage] = useState(1);
    const [serverLimit, setServerLimit] = useState(50);
    const [classAttendanceSummary, setClassAttendanceSummary] = useState([]);
    const [loadingClassSummary, setLoadingClassSummary] = useState(false);
    const [serverPagination, setServerPagination] = useState({
        page: 1,
        limit: 50,
        offset: 0,
        total_records: 0,
        total_pages: 1,
        has_next: false,
        has_prev: false,
    });

    const headers = useMemo(
        () => ({
            'Content-Type': 'application/json',
            Accept: 'application/json',
            token: localStorage.getItem('token'),
        }),
        []
    );

    const fetchSchoolEventsByDate = async (date, page = 1, limit = 20) => {
        setLoadingEvents(true);

        try {
            const formattedDate = dayjs(date).format('YYYY-MM-DD');

            const res = await API.get(`/customAttendance/events/bySchoolDate`, {
                headers,
                params: {
                    date: formattedDate,
                    type_id: classId,
                    page,
                    limit,
                },
            });

            if (res.data.response_code === 1000) {
                const payload = res.data.data || {};

                const items = Array.isArray(payload)
                    ? payload
                    : Array.isArray(payload.items)
                        ? payload.items
                        : [];

                const pagination = payload.pagination || {
                    page,
                    limit,
                    offset: (page - 1) * limit,
                    total_records: items.reduce(
                        (sum, group) => sum + ((group.students || []).length),
                        0
                    ),
                    total_pages: 1,
                    has_next: false,
                    has_prev: page > 1,
                };

                setEventRecords(items);
                setServerPagination(pagination);
                setServerPage(pagination.page || page);
                setServerLimit(pagination.limit || limit);

                // Reset local page because backend already changed page
                setCurrentPage(1);

                const firstEventName =
                    items?.[0]?.students?.[0]?.type_name ||
                    items?.[0]?.students?.[0]?.event_name ||
                    '';

                if (firstEventName) {
                    setClassName(firstEventName);
                }
            } else {
                toast.error(res.data.message || 'Failed to load event records');
            }
        } catch (error) {
            console.error('❌ Error fetching event records:', error);
            toast.error('Error fetching event records');
        } finally {
            setLoadingEvents(false);
        }
    };

    const fetchMonthlySummary = async (date) => {
        try {
            const month = dayjs(date).format('YYYY-MM');

            const res = await API.get(`/customAttendance/events/monthlySummary`, {
                headers,
                params: {
                    month,
                    type_id: classId,
                },
            });

            if (res.data.response_code === 1000) {
                setMonthlySummary(res.data.data || []);
            } else {
                toast.error(res.data.message || 'Failed to load monthly summary');
            }
        } catch (error) {
            console.error('❌ Error fetching monthly summary:', error);
            toast.error('Error fetching monthly summary');
        }
    };
    const fetchClassAttendanceAnalytics = async (date) => {
        setLoadingClassSummary(true);

        try {
            const formattedDate = dayjs(date).format('YYYY-MM-DD');

            const res = await API.get(`/customAttendance/events/classAnalyticsByDate`, {
                headers,
                params: {
                    date: formattedDate,
                    type_id: classId,
                },
            });

            if (res.data.response_code === 1000) {
                const payload = res.data.data || {};

                const items =
                    payload.classAttendanceSummary ||
                    payload.classes ||
                    [];

                setClassAttendanceSummary(Array.isArray(items) ? items : []);
            } else {
                toast.error(res.data.message || 'Failed to load class attendance analytics');
                setClassAttendanceSummary([]);
            }
        } catch (error) {
            console.error('❌ Error fetching class attendance analytics:', error);
            toast.error('Error fetching class attendance analytics');
            setClassAttendanceSummary([]);
        } finally {
            setLoadingClassSummary(false);
        }
    };
    const [downloadingPdf, setDownloadingPdf] = useState(false);


    useEffect(() => {
        setServerPage(1);
        fetchSchoolEventsByDate(selectedDate, 1, serverLimit);
        fetchMonthlySummary(selectedDate);
        fetchClassAttendanceAnalytics(selectedDate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classId, selectedDate]);

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedDate, selectedClassFilter, searchTerm, pageSize]);

    const groupedRecords = useMemo(() => {
        const safeEventRecords = Array.isArray(eventRecords) ? eventRecords : [];

        return safeEventRecords.reduce((acc, group) => {
            const clsName = group.class_name || 'No Class';
            const students = Array.isArray(group.students) ? group.students : [];

            if (!acc[clsName]) {
                acc[clsName] = [];
            }

            acc[clsName] = [...acc[clsName], ...students];

            return acc;
        }, {});
    }, [eventRecords]);
    const classOptions = useMemo(() => {
        return Object.keys(groupedRecords).sort((a, b) => a.localeCompare(b));
    }, [groupedRecords]);

    const filteredGroupedRecords = useMemo(() => {
        let result = groupedRecords;

        if (selectedClassFilter !== 'ALL') {
            result = {
                [selectedClassFilter]: groupedRecords[selectedClassFilter] || [],
            };
        }

        const term = searchTerm.trim().toLowerCase();

        return Object.fromEntries(
            Object.entries(result)
                .map(([clsName, students]) => [
                    clsName,
                    students.filter((s) => {
                        if (!term) return true;

                        return (
                            s.student_name?.toLowerCase().includes(term) ||
                            s.student_uuid?.toLowerCase().includes(term) ||
                            s.class_name?.toLowerCase().includes(term) ||
                            s.gate?.toLowerCase().includes(term)
                        );
                    }),
                ])
                .filter(([, students]) => students.length > 0)
        );
    }, [groupedRecords, selectedClassFilter, searchTerm]);

    const flattenedFilteredRecords = useMemo(() => {
        return Object.entries(filteredGroupedRecords).flatMap(([clsName, students]) =>
            students.map((student) => ({
                clsName,
                student,
            }))
        );
    }, [filteredGroupedRecords]);


    const paginatedGroupedRecords = useMemo(() => {
        return filteredGroupedRecords;
    }, [filteredGroupedRecords]);


    const totalStudentsForSelectedDate = useMemo(() => {
        return Object.values(groupedRecords).reduce(
            (sum, students) => sum + students.length,
            0
        );
    }, [groupedRecords]);

    const visibleStudentsCount = useMemo(() => {
        return Object.values(filteredGroupedRecords).reduce(
            (sum, students) => sum + students.length,
            0
        );
    }, [filteredGroupedRecords]);

    const totalClassesForSelectedDate = useMemo(() => {
        return Object.keys(groupedRecords).length;
    }, [groupedRecords]);

    const selectedDateSummary = useMemo(() => {
        const formattedDate = dayjs(selectedDate).format('YYYY-MM-DD');
        return monthlySummary.find((item) => item.date === formattedDate) || null;
    }, [monthlySummary, selectedDate]);

    const totalCheckIns = selectedDateSummary?.check_in_count || 0;
    const totalCheckOuts = selectedDateSummary?.check_out_count || 0;

    const uniqueStudents =
        selectedDateSummary?.unique_students || totalStudentsForSelectedDate;

    const ClassSummaryCard = ({ item }) => {
        const className = item.className || item.class_name || 'No Class';

        const total =
            item.totalRecords ??
            item.total_records ??
            item.total ??
            0;

        const totalStudents =
            item.totalStudents ??
            item.total_students ??
            item.unique_students ??
            total;

        const checkInCount =
            item.checkInCount ??
            item.check_in_count ??
            0;

        const checkOutCount =
            item.checkOutCount ??
            item.check_out_count ??
            0;

        const completedCount =
            item.completedCount ??
            item.completed_count ??
            0;

        const openCheckinCount =
            item.openCheckinCount ??
            item.open_checkin_count ??
            0;

        return (
            <div
                style={{
                    ...cardStyle,
                    padding: '1rem',
                    height: '100%',
                }}
            >
                <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
                    <div style={{ minWidth: 0 }}>
                        <div
                            style={{
                                fontSize: '1rem',
                                fontWeight: 800,
                                color: '#0f172a',
                                lineHeight: 1.2,
                            }}
                        >
                            {className}
                        </div>
                        <div style={{ ...mutedStyle, marginTop: 4 }}>
                            Class attendance summary
                        </div>
                    </div>

                    <div
                        style={{
                            padding: '6px 10px',
                            borderRadius: 999,
                            background: '#eff6ff',
                            color: '#1d4ed8',
                            border: '1px solid #bfdbfe',
                            fontWeight: 800,
                            fontSize: '0.78rem',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {total} record{Number(total) !== 1 ? 's' : ''}
                    </div>
                </div>

                <div className="row g-2">
                    <div className="col-4">
                        <div
                            style={{
                                borderRadius: 14,
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                padding: '0.75rem',
                            }}
                        >
                            <div style={{ ...sectionTitleStyle, marginBottom: 6 }}>Students</div>
                            <div style={{ fontSize: '1.35rem', fontWeight: 900, color: '#020617' }}>
                                {totalStudents}
                            </div>
                        </div>
                    </div>

                    <div className="col-4">
                        <div
                            style={{
                                borderRadius: 14,
                                background: 'rgba(34, 197, 94, 0.08)',
                                border: '1px solid rgba(34, 197, 94, 0.16)',
                                padding: '0.75rem',
                            }}
                        >
                            <div style={{ ...sectionTitleStyle, marginBottom: 6, color: '#16a34a' }}>
                                In
                            </div>
                            <div style={{ fontSize: '1.35rem', fontWeight: 900, color: '#166534' }}>
                                {checkInCount}
                            </div>
                        </div>
                    </div>

                    <div className="col-4">
                        <div
                            style={{
                                borderRadius: 14,
                                background: 'rgba(6, 182, 212, 0.08)',
                                border: '1px solid rgba(6, 182, 212, 0.16)',
                                padding: '0.75rem',
                            }}
                        >
                            <div style={{ ...sectionTitleStyle, marginBottom: 6, color: '#0891b2' }}>
                                Out
                            </div>
                            <div style={{ fontSize: '1.35rem', fontWeight: 900, color: '#0e7490' }}>
                                {checkOutCount}
                            </div>
                        </div>
                    </div>

                    <div className="col-6">
                        <div
                            style={{
                                borderRadius: 14,
                                background: 'rgba(59, 130, 246, 0.08)',
                                border: '1px solid rgba(59, 130, 246, 0.16)',
                                padding: '0.75rem',
                            }}
                        >
                            <div style={{ ...sectionTitleStyle, marginBottom: 6, color: '#2563eb' }}>
                                Complete
                            </div>
                            <div style={{ fontSize: '1.2rem', fontWeight: 900, color: '#1d4ed8' }}>
                                {completedCount}
                            </div>
                        </div>
                    </div>

                    <div className="col-6">
                        <div
                            style={{
                                borderRadius: 14,
                                background: 'rgba(245, 158, 11, 0.1)',
                                border: '1px solid rgba(245, 158, 11, 0.18)',
                                padding: '0.75rem',
                            }}
                        >
                            <div style={{ ...sectionTitleStyle, marginBottom: 6, color: '#d97706' }}>
                                Still In
                            </div>
                            <div style={{ fontSize: '1.2rem', fontWeight: 900, color: '#92400e' }}>
                                {openCheckinCount}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const tileContent = ({ date, view }) => {
        if (view !== 'month') return null;

        const formattedDate = dayjs(date).format('YYYY-MM-DD');
        const record = monthlySummary.find((item) => item.date === formattedDate);

        if (!record) return null;

        if ((record.check_in_count || 0) === 0 && (record.check_out_count || 0) === 0) {
            return null;
        }

        return (
            <div
                style={{
                    marginTop: 4,
                    display: 'flex',
                    justifyContent: 'center',
                }}
            >
                <div
                    style={{
                        minWidth: 22,
                        height: 22,
                        padding: '0 7px',
                        borderRadius: 999,
                        background: '#dbeafe',
                        color: '#1d4ed8',
                        fontSize: '0.72rem',
                        fontWeight: 800,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {record.unique_students || 0}
                </div>
            </div>
        );
    };

    const parseJsonSafe = (value, fallback = null) => {
        try {
            if (!value) return fallback;
            if (typeof value === 'object') return value;
            return JSON.parse(value);
        } catch {
            return fallback;
        }
    };

    const normalizeImageSrc = (src) => {
        if (!src) return null;

        const value = String(src).trim();

        if (!value) return null;

        if (value.startsWith('data:image/png;base64,/9j/')) {
            return value.replace('data:image/png;base64,', 'data:image/jpeg;base64,');
        }

        if (value.startsWith('data:image/')) {
            return value;
        }

        if (value.startsWith('/9j/')) {
            return `data:image/jpeg;base64,${value}`;
        }

        if (value.startsWith('iVBORw0KGgo')) {
            return `data:image/png;base64,${value}`;
        }

        return value;
    };

    const parseFlowPayload = (s) => {
        return parseJsonSafe(s?.flow_payload, {});
    };

    const getAttachments = (s) => {
        if (Array.isArray(s?.attachments)) return s.attachments;
        return parseJsonSafe(s?.attachments, []);
    };

    const getAttachmentSrc = (a) => {
        return normalizeImageSrc(
            a?.image ||
            a?.attachment_url ||
            a?.image_url ||
            a?.url ||
            null
        );
    };

    const getMainImage = (s) => {
        const payload = parseFlowPayload(s);
        const attachments = getAttachments(s);

        const scanAttachment = attachments.find((a) => {
            return String(a?.step_key || '').toLowerCase() === 'scan';
        });

        const checkinAttachment = attachments.find((a) => {
            return String(a?.step_key || '').toLowerCase() === 'checkin_photo';
        });

        return normalizeImageSrc(
            getAttachmentSrc(scanAttachment) ||
            payload?.scan?.url ||
            getAttachmentSrc(checkinAttachment) ||
            payload?.checkin_photo?.url ||
            s?.scan_image ||
            s?.scan_image_url ||
            s?.image ||
            s?.image_url ||
            s?.checkin_photo_url ||
            s?.check_in_image ||
            null
        );
    };

    const formatPhotoLabel = (value) => {
        const key = String(value || '').trim();

        if (!key) return 'Photo';

        const labelMap = {
            checkin_photo: 'Check-in',
            photo: 'Photo',
            qr: 'QR',
            signature: 'Signature',
            remarks: 'Remarks',
        };

        if (labelMap[key]) return labelMap[key];

        return key
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const getStepImageLabel = (s) => {
        const payload = parseFlowPayload(s);
        const attachments = getAttachments(s);

        const photoAttachment = attachments.find((a) => {
            return String(a?.step_key || '').toLowerCase() === 'photo';
        });

        return formatPhotoLabel(
            photoAttachment?.step_key ||
            payload?.photo?.label ||
            payload?.photo?.step_key ||
            'photo'
        );
    };

    const getStepImage = (s) => {
        const payload = parseFlowPayload(s);
        const attachments = getAttachments(s);

        const photoAttachment = attachments.find((a) => {
            return String(a?.step_key || '').toLowerCase() === 'photo';
        });

        return normalizeImageSrc(
            getAttachmentSrc(photoAttachment) ||
            payload?.photo?.url ||
            s?.photo_url ||
            null
        );
    };
    const sendPdfToEmail = async () => {
        setDownloadingPdf(true);

        try {
            const formattedDate = dayjs(selectedDate).format('YYYY-MM-DD');

            const res = await API.post(
                `/customAttendance/events/bySchoolDate/pdf/full/images/email`,
                {
                    date: formattedDate,
                    type_id: classId,
                },
                {
                    headers
                }
            );

            if (res.data.response_code === 1000) {
                toast.success('PDF report is being generated and will be sent to your email shortly');
            } else {
                toast.error(res.data.message || 'Failed to send PDF email');
            }
        } catch (error) {
            console.error('❌ PDF email error:', error);
            toast.error(error?.response?.data?.message || 'Failed to send PDF email');
        } finally {
            setDownloadingPdf(false);
        }
    };
    const PhotoThumb = ({ src, label }) => {
        const safeSrc = normalizeImageSrc(src);

        if (!safeSrc) {
            return (
                <div
                    style={{
                        width: 84,
                        minHeight: 108,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                    }}
                >
                    <div
                        style={{
                            width: 84,
                            height: 84,
                            borderRadius: 12,
                            border: '1px dashed #cbd5e1',
                            background: '#f8fafc',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#94a3b8',
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            textAlign: 'center',
                            padding: 8,
                        }}
                    >
                        No image
                    </div>

                    <small
                        style={{
                            color: '#64748b',
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {label}
                    </small>
                </div>
            );
        }

        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 6,
                }}
            >
                <button
                    type="button"
                    onClick={() => setPreviewImage(safeSrc)}
                    style={{
                        border: 'none',
                        padding: 0,
                        background: 'transparent',
                        cursor: 'zoom-in',
                    }}
                >
                    <img
                        src={safeSrc}
                        alt={label}
                        onError={(e) => {
                            console.error('Image failed to load:', {
                                label,
                                src: safeSrc,
                            });

                            e.currentTarget.style.opacity = '0.25';
                            e.currentTarget.style.objectFit = 'contain';
                        }}
                        style={{
                            width: 84,
                            height: 84,
                            borderRadius: 12,
                            objectFit: 'cover',
                            boxShadow: '0 8px 20px rgba(15,23,42,0.1)',
                            border: '1px solid #e2e8f0',
                            background: '#f8fafc',
                        }}
                    />
                </button>

                <small
                    style={{
                        color: '#64748b',
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                    }}
                >
                    {label}
                </small>
            </div>
        );
    };
    const PaginationControls = () => {
        const totalRecords = Number(serverPagination?.total_records || 0);
        const totalServerPages = Number(serverPagination?.total_pages || 1);
        const activePage = Number(serverPagination?.page || serverPage || 1);
        const activeLimit = Number(serverPagination?.limit || serverLimit || 50);

        if (totalRecords === 0) return null;

        const startRecord =
            totalRecords === 0 ? 0 : ((activePage - 1) * activeLimit) + 1;

        const endRecord = Math.min(activePage * activeLimit, totalRecords);

        const pageNumbers = [];
        const startPage = Math.max(1, activePage - 2);
        const endPage = Math.min(totalServerPages, activePage + 2);

        for (let page = startPage; page <= endPage; page += 1) {
            pageNumbers.push(page);
        }

        const goToServerPage = (nextPage) => {
            const safePage = Math.min(Math.max(1, nextPage), totalServerPages);

            if (safePage === activePage) return;

            fetchSchoolEventsByDate(selectedDate, safePage, activeLimit);
        };

        const changeServerLimit = (nextLimit) => {
            setServerLimit(nextLimit);
            setPageSize(nextLimit);
            setCurrentPage(1);
            fetchSchoolEventsByDate(selectedDate, 1, nextLimit);
        };

        return (
            <div
                className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3 my-3"
                style={{
                    padding: '1rem',
                    borderRadius: 16,
                    background: '#f8fafc',
                    border: '1px solid #e9eef5',
                }}
            >
                <div style={{ color: '#475569', fontWeight: 700 }}>
                    Showing {startRecord}–{endRecord} of {totalRecords} record
                    {totalRecords !== 1 ? 's' : ''}
                </div>

                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <select
                        className="form-select"
                        value={activeLimit}
                        onChange={(e) => changeServerLimit(Number(e.target.value))}
                        style={{
                            width: 120,
                            borderRadius: 12,
                            fontWeight: 700,
                        }}
                    >
                        <option value={25}>25 / page</option>
                        <option value={50}>50 / page</option>
                        <option value={100}>100 / page</option>
                        <option value={200}>200 / page</option>
                    </select>

                    <button
                        type="button"
                        className="btn btn-light"
                        disabled={activePage <= 1 || loadingEvents}
                        onClick={() => goToServerPage(1)}
                        style={{ borderRadius: 12, fontWeight: 800 }}
                    >
                        First
                    </button>

                    <button
                        type="button"
                        className="btn btn-light"
                        disabled={activePage <= 1 || loadingEvents}
                        onClick={() => goToServerPage(activePage - 1)}
                        style={{ borderRadius: 12, fontWeight: 800 }}
                    >
                        Prev
                    </button>

                    {pageNumbers.map((page) => (
                        <button
                            key={page}
                            type="button"
                            className={page === activePage ? 'btn btn-primary' : 'btn btn-light'}
                            disabled={loadingEvents}
                            onClick={() => goToServerPage(page)}
                            style={{
                                borderRadius: 12,
                                fontWeight: 800,
                                minWidth: 42,
                            }}
                        >
                            {page}
                        </button>
                    ))}

                    <button
                        type="button"
                        className="btn btn-light"
                        disabled={activePage >= totalServerPages || loadingEvents}
                        onClick={() => goToServerPage(activePage + 1)}
                        style={{ borderRadius: 12, fontWeight: 800 }}
                    >
                        Next
                    </button>

                    <button
                        type="button"
                        className="btn btn-light"
                        disabled={activePage >= totalServerPages || loadingEvents}
                        onClick={() => goToServerPage(totalServerPages)}
                        style={{ borderRadius: 12, fontWeight: 800 }}
                    >
                        Last
                    </button>
                </div>
            </div>
        );
    };

    return (
        <Fragment>
            <div className="container-fluid py-3">
                <div className="row g-3">
                    <div className="col-12">
                        <div
                            style={{
                                ...cardStyle,
                                padding: '1.25rem',
                                background: '#fff',
                            }}
                        >
                            <div className="d-flex flex-column flex-xl-row justify-content-between align-items-start gap-3">
                                <div>
                                    <div
                                        className="mb-2"
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            padding: '6px 12px',
                                            borderRadius: 999,
                                            background: '#eef6ff',
                                            color: '#2563eb',
                                            fontWeight: 700,
                                            fontSize: '0.8rem',
                                        }}
                                    >
                                        Custom Attendance Event
                                    </div>

                                    <h2
                                        className="mb-1"
                                        style={{
                                            fontSize: '1.7rem',
                                            fontWeight: 800,
                                            color: '#0f172a',
                                        }}
                                    >
                                        {className || 'Event Details'}
                                    </h2>

                                    <div style={mutedStyle}>
                                        {dayjs(selectedDate).format('dddd, DD MMMM YYYY')}
                                    </div>
                                </div>

                                <div
                                    style={{
                                        padding: '0.85rem 1rem',
                                        borderRadius: 14,
                                        background: '#f8fafc',
                                        border: '1px solid #e5edf5',
                                        minWidth: 240,
                                    }}
                                >
                                    <div style={sectionTitleStyle}>Selected event date</div>
                                    <div style={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a' }}>
                                        {dayjs(selectedDate).format('DD MMM YYYY')}
                                    </div>
                                    <div style={{ ...mutedStyle, marginTop: 4 }}>
                                        {visibleStudentsCount} visible record
                                        {visibleStudentsCount !== 1 ? 's' : ''}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={sendPdfToEmail}
                                    disabled={downloadingPdf}
                                    style={{
                                        borderRadius: 14,
                                        minHeight: 48,
                                        fontWeight: 800,
                                        padding: '0 18px',
                                        boxShadow: '0 10px 24px rgba(37, 99, 235, 0.2)',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {downloadingPdf ? 'Sending...' : 'Send PDF to Email'}
                                </button>
                            </div>

                            <div className="row g-2 mt-1">
                                <div className="col-12 col-md-6 col-xl-3">
                                    <SummaryCard
                                        label="Unique students"
                                        value={uniqueStudents}
                                        subtext="Recorded on selected date"
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-xl-3">
                                    <SummaryCard
                                        label="Check-ins"
                                        value={totalCheckIns}
                                        subtext="Successful entries"
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-xl-3">
                                    <SummaryCard
                                        label="Check-outs"
                                        value={totalCheckOuts}
                                        subtext="Successful exits"
                                    />
                                </div>

                                <div className="col-12 col-md-6 col-xl-3">
                                    <SummaryCard
                                        label="Classes"
                                        value={totalClassesForSelectedDate}
                                        subtext="Classes involved"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-12">
                        <div className="row g-3">
                            <div className="col-12">
                                <div style={{ ...cardStyle, padding: '1rem' }}>
                                    <div className="mb-3">
                                        <h5 className="mb-1" style={{ fontWeight: 800, color: '#0f172a' }}>
                                            Attendance Calendar
                                        </h5>
                                        <div style={mutedStyle}>
                                            Select a date to refresh the records
                                        </div>
                                    </div>

                                    <div className="calendar-modern modern-calendar-shell">
                                        <Calendar
                                            value={selectedDate}
                                            onChange={(date) => setSelectedDate(date)}
                                            tileContent={tileContent}
                                        />
                                    </div>
                                </div>
                            </div>

                            {classAttendanceSummary.length > 0 && (
                                <div className="col-12">
                                    <div style={{ ...cardStyle, padding: '1rem' }}>
                                        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3 mb-3">
                                            <div>
                                                <h5 className="mb-1" style={{ fontWeight: 800, color: '#0f172a' }}>
                                                    Attendance by Class
                                                </h5>
                                                <div style={mutedStyle}>
                                                    Summary of attendance distribution for the selected date
                                                </div>
                                            </div>

                                            <div
                                                className="px-3 py-2"
                                                style={{
                                                    borderRadius: 12,
                                                    background: '#f8fafc',
                                                    border: '1px solid #e2e8f0',
                                                    fontWeight: 800,
                                                    color: '#334155',
                                                }}
                                            >
                                                {classAttendanceSummary.length} class
                                                {classAttendanceSummary.length !== 1 ? 'es' : ''}
                                            </div>
                                        </div>

                                        <div className="row g-3">
                                            {classAttendanceSummary.map((item) => (
                                                <div
                                                    key={item.className}
                                                    className="col-12 col-md-6 col-xl-4 col-xxl-3"
                                                >
                                                    <ClassSummaryCard item={item} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="col-12">
                                <div style={{ ...cardStyle, padding: '1rem 1rem 1.15rem', height: '100%' }}>
                                    <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3 mb-3">
                                        <div>
                                            <h5 className="mb-1" style={{ fontWeight: 800, color: '#0f172a' }}>
                                                Student Records
                                            </h5>
                                            <div style={mutedStyle}>
                                                Full-width list for better visibility
                                            </div>
                                        </div>

                                        <div
                                            className="px-3 py-2"
                                            style={{
                                                borderRadius: 12,
                                                background: '#f8fafc',
                                                border: '1px solid #e2e8f0',
                                                fontWeight: 800,
                                                color: '#334155',
                                            }}
                                        >
                                            Total records: {serverPagination?.total_records || totalStudentsForSelectedDate}
                                        </div>
                                    </div>

                                    <TabContent activeTab={activeSubTab}>
                                        <TabPane tabId="students">
                                            {loadingEvents ? (
                                                <div className="text-center py-5">
                                                    <div
                                                        className="spinner-border text-primary mb-3"
                                                        role="status"
                                                        style={{ width: '3rem', height: '3rem' }}
                                                    />
                                                    <h6 className="fw-semibold text-muted">Loading records...</h6>
                                                    <small className="text-muted">
                                                        Please wait while data is being fetched.
                                                    </small>
                                                </div>
                                            ) : Object.keys(groupedRecords).length > 0 ? (
                                                <>
                                                    <div
                                                        className="mb-4"
                                                        style={{
                                                            padding: '1rem',
                                                            borderRadius: 16,
                                                            background: '#f8fafc',
                                                            border: '1px solid #e9eef5',
                                                        }}
                                                    >
                                                        <div className="row g-3">
                                                            <div className="col-12 col-lg-4">
                                                                <label className="form-label fw-semibold mb-2">
                                                                    Filter by class
                                                                </label>
                                                                <select
                                                                    className="form-select"
                                                                    value={selectedClassFilter}
                                                                    onChange={(e) =>
                                                                        setSelectedClassFilter(e.target.value)
                                                                    }
                                                                    style={{
                                                                        borderRadius: 12,
                                                                        minHeight: 46,
                                                                    }}
                                                                >
                                                                    <option value="ALL">
                                                                        All Classes ({totalStudentsForSelectedDate})
                                                                    </option>
                                                                    {classOptions.map((cls) => (
                                                                        <option key={cls} value={cls}>
                                                                            {cls} ({groupedRecords[cls]?.length || 0})
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </div>

                                                            <div className="col-12 col-lg-8">
                                                                <label className="form-label fw-semibold mb-2">
                                                                    Search student
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    placeholder="Search by student name, ID, class, or gate..."
                                                                    value={searchTerm}
                                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                                    style={{
                                                                        borderRadius: 12,
                                                                        minHeight: 46,
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {Object.keys(filteredGroupedRecords).length === 0 ? (
                                                        <EmptyState
                                                            title="No matching students"
                                                            subtitle="Try changing the class filter or search keyword."
                                                        />
                                                    ) : (
                                                        <>
                                                            <PaginationControls />

                                                            {Object.entries(paginatedGroupedRecords).map(
                                                                ([clsName, students]) => (
                                                                    <div key={clsName} className="mb-4">
                                                                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-3">
                                                                            <div>
                                                                                <h5
                                                                                    className="mb-1"
                                                                                    style={{
                                                                                        fontWeight: 800,
                                                                                        color: '#0f172a',
                                                                                    }}
                                                                                >
                                                                                    {clsName}
                                                                                </h5>
                                                                                <div style={mutedStyle}>
                                                                                    Student attendance records for this class
                                                                                </div>
                                                                            </div>

                                                                            <div
                                                                                className="px-3 py-2"
                                                                                style={{
                                                                                    borderRadius: 999,
                                                                                    background: '#eff6ff',
                                                                                    color: '#1d4ed8',
                                                                                    fontWeight: 800,
                                                                                    fontSize: '0.86rem',
                                                                                    border: '1px solid #bfdbfe',
                                                                                }}
                                                                            >
                                                                                {students.length} shown on this page
                                                                            </div>
                                                                        </div>

                                                                        <div
                                                                            style={{
                                                                                borderRadius: 18,
                                                                                border: '1px solid #edf2f7',
                                                                                overflow: 'hidden',
                                                                                background: '#fff',
                                                                            }}
                                                                        >
                                                                            <div
                                                                                style={{
                                                                                    width: '100%',
                                                                                    overflowX: 'auto',
                                                                                }}
                                                                            >
                                                                                <Table
                                                                                    hover
                                                                                    className="mb-0 align-middle"
                                                                                    style={{
                                                                                        background: '#fff',
                                                                                        minWidth: 1220,
                                                                                        tableLayout: 'auto',
                                                                                    }}
                                                                                >
                                                                                    <thead
                                                                                        style={{
                                                                                            background: '#f8fafc',
                                                                                        }}
                                                                                    >
                                                                                        <tr>
                                                                                            <th style={{ width: 60 }}>#</th>
                                                                                            <th style={{ minWidth: 260 }}>Student</th>
                                                                                            <th style={{ minWidth: 140 }}>Class</th>
                                                                                            <th style={{ minWidth: 130 }}>Status</th>
                                                                                            <th style={{ minWidth: 120 }}>Gate</th>
                                                                                            <th style={{ minWidth: 180 }}>Timestamp</th>
                                                                                            <th style={{ minWidth: 230 }}>Photos</th>
                                                                                        </tr>
                                                                                    </thead>

                                                                                    <tbody>
                                                                                        {students.map((s, i) => {
                                                                                            const status =
                                                                                                STATUS_META[s.status] ||
                                                                                                STATUS_META.default;
                                                                                            const backendOffset = Number(serverPagination?.offset || 0);
                                                                                            const localIndex = flattenedFilteredRecords.findIndex(
                                                                                                (item) =>
                                                                                                    item.student === s &&
                                                                                                    item.clsName === clsName
                                                                                            );

                                                                                            const globalRowNumber = backendOffset + localIndex + 1;

                                                                                            const mainImage = getMainImage(s);
                                                                                            const stepImage = getStepImage(s);

                                                                                            return (
                                                                                                <tr
                                                                                                    key={`${s.custom_attendance_id ||
                                                                                                        s.student_uuid ||
                                                                                                        i
                                                                                                        }-${s.timestamp || i}`}
                                                                                                >
                                                                                                    <td className="fw-semibold text-muted">
                                                                                                        {globalRowNumber}
                                                                                                    </td>

                                                                                                    <td>
                                                                                                        <div className="d-flex align-items-center gap-3">
                                                                                                            <div
                                                                                                                style={{
                                                                                                                    width: 42,
                                                                                                                    height: 42,
                                                                                                                    borderRadius: 12,
                                                                                                                    background: '#eff6ff',
                                                                                                                    display: 'flex',
                                                                                                                    alignItems: 'center',
                                                                                                                    justifyContent: 'center',
                                                                                                                    fontWeight: 800,
                                                                                                                    color: '#2563eb',
                                                                                                                    flexShrink: 0,
                                                                                                                }}
                                                                                                            >
                                                                                                                {(s.student_name || '?')
                                                                                                                    .charAt(0)
                                                                                                                    .toUpperCase()}
                                                                                                            </div>

                                                                                                            <div style={{ minWidth: 0 }}>
                                                                                                                <div
                                                                                                                    className="fw-semibold"
                                                                                                                    style={{
                                                                                                                        color: '#0f172a',
                                                                                                                        whiteSpace: 'nowrap',
                                                                                                                    }}
                                                                                                                >
                                                                                                                    {s.student_name || '-'}
                                                                                                                </div>
                                                                                                                <small
                                                                                                                    className="text-muted"
                                                                                                                    style={{
                                                                                                                        display: 'block',
                                                                                                                        whiteSpace: 'nowrap',
                                                                                                                    }}
                                                                                                                >
                                                                                                                    {s.student_uuid || '-'}
                                                                                                                </small>
                                                                                                            </div>
                                                                                                        </div>
                                                                                                    </td>

                                                                                                    <td>
                                                                                                        <span
                                                                                                            className="fw-semibold"
                                                                                                            style={{
                                                                                                                color: '#334155',
                                                                                                                whiteSpace: 'nowrap',
                                                                                                            }}
                                                                                                        >
                                                                                                            {s.class_name || clsName || '-'}
                                                                                                        </span>
                                                                                                    </td>

                                                                                                    <td>
                                                                                                        <span
                                                                                                            className={`badge ${status.className}`}
                                                                                                            style={{
                                                                                                                padding: '8px 12px',
                                                                                                                borderRadius: 999,
                                                                                                                fontSize: '0.8rem',
                                                                                                                fontWeight: 700,
                                                                                                                whiteSpace: 'nowrap',
                                                                                                            }}
                                                                                                        >
                                                                                                            {status.label}
                                                                                                        </span>
                                                                                                    </td>

                                                                                                    <td>
                                                                                                        <span
                                                                                                            style={{
                                                                                                                color: '#475569',
                                                                                                                fontWeight: 600,
                                                                                                                whiteSpace: 'nowrap',
                                                                                                            }}
                                                                                                        >
                                                                                                            {s.gate || '-'}
                                                                                                        </span>
                                                                                                    </td>

                                                                                                    <td>
                                                                                                        <span
                                                                                                            style={{
                                                                                                                whiteSpace: 'nowrap',
                                                                                                                fontSize: '0.85rem',
                                                                                                                color: '#475569',
                                                                                                            }}
                                                                                                        >
                                                                                                            {s.timestamp || '-'}
                                                                                                        </span>
                                                                                                    </td>

                                                                                                    <td>
                                                                                                        {!mainImage && !stepImage ? (
                                                                                                            <span className="text-muted">
                                                                                                                No image
                                                                                                            </span>
                                                                                                        ) : (
                                                                                                            <div
                                                                                                                style={{
                                                                                                                    display: 'flex',
                                                                                                                    gap: 10,
                                                                                                                    alignItems: 'flex-start',
                                                                                                                    minWidth: 200,
                                                                                                                }}
                                                                                                            >
                                                                                                                <PhotoThumb
                                                                                                                    src={mainImage}
                                                                                                                    label="Scan"
                                                                                                                />

                                                                                                                <PhotoThumb
                                                                                                                    src={stepImage}
                                                                                                                    label={getStepImageLabel(s)}
                                                                                                                />
                                                                                                            </div>
                                                                                                        )}
                                                                                                    </td>
                                                                                                </tr>
                                                                                            );
                                                                                        })}
                                                                                    </tbody>
                                                                                </Table>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )
                                                            )}

                                                            <PaginationControls />
                                                        </>
                                                    )}
                                                </>
                                            ) : (
                                                <EmptyState
                                                    title="No students for this date"
                                                    subtitle="Try selecting a different date on the calendar."
                                                />
                                            )}
                                        </TabPane>
                                    </TabContent>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ImagePreviewModal image={previewImage} onClose={() => setPreviewImage(null)} />

            <style>
                {`
                    .modern-calendar-shell .react-calendar {
                        width: 100%;
                        border: none;
                        background: transparent;
                        font-family: inherit;
                    }

                    .modern-calendar-shell .react-calendar__navigation {
                        margin-bottom: 1rem;
                    }

                    .modern-calendar-shell .react-calendar__navigation button {
                        border-radius: 12px;
                        min-width: 42px;
                        background: #f8fafc;
                        color: #0f172a;
                        border: 1px solid #e2e8f0;
                        font-weight: 700;
                    }

                    .modern-calendar-shell .react-calendar__month-view__weekdays__weekday {
                        padding: 0.6rem 0;
                        font-size: 0.78rem;
                        text-transform: uppercase;
                        color: #64748b;
                    }

                    .modern-calendar-shell .react-calendar__tile {
                        border-radius: 14px;
                        padding: 0.75rem 0.35rem;
                        min-height: 72px;
                        background: transparent;
                        transition: all 0.2s ease;
                    }

                    .modern-calendar-shell .react-calendar__tile:hover {
                        background: #f8fafc;
                    }

                    .modern-calendar-shell .react-calendar__tile--active {
                        background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%) !important;
                        color: white;
                    }

                    .modern-calendar-shell .react-calendar__tile--now {
                        background: #eff6ff;
                        color: #1d4ed8;
                    }

                    .bg-success-subtle {
                        background-color: rgba(34, 197, 94, 0.12) !important;
                    }

                    .bg-info-subtle {
                        background-color: rgba(6, 182, 212, 0.12) !important;
                    }

                    .border-success-subtle {
                        border-color: rgba(34, 197, 94, 0.2) !important;
                    }

                    .border-info-subtle {
                        border-color: rgba(6, 182, 212, 0.2) !important;
                    }
                `}
            </style>
        </Fragment>
    );
};

export default CustomCheckerDetailPage;